import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Target, TrendingUp, Users, Shield } from 'lucide-react';
export function OrganizationalLevelPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1329">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1330">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1331">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1332">
            <span className="w-8 h-px bg-teal" data-id="element-1333"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1334">
              Scale
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1335"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1336">
            Organizational Level
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-1337">
            At the organizational level, NCJI is designed to support readiness
            and capability-building inside institutions. It helps organizations
            understand the human factors that shape performance, stability,
            leadership development, and long-term resilience.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1338">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1339">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1340">
            Rather than looking only at outputs or credentials, the framework
            helps reveal how reasoning, self-direction, emotional regulation,
            cooperation, and responsibility influence real institutional
            effectiveness.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1341">
            Organizations depend on more than technical ability. They rely on
            people who can think clearly, handle pressure, communicate well,
            coordinate with others, and grow into broader responsibilities over
            time. NCJI helps make those capabilities more visible, more
            structured, and more actionable so institutions can better develop
            talent, strengthen teams, and build more reliable leadership
            pipelines.
          </p>
        </motion.div>
      </section>

      {/* Readiness Inside Institutions */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-1342">
        <div className="max-w-4xl mx-auto" data-id="element-1343">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1344">
            <div className="flex items-center gap-4 mb-6" data-id="element-1345">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1346">
                <Target size={24} data-id="element-1347" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1348">
                Readiness Inside Institutions
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1349">
              NCJI helps organizations understand where readiness is strong and
              where it is still forming. This includes the ability of people
              within the institution to reason well, manage pressure, follow
              through, cooperate effectively, and act responsibly inside systems
              that require trust and coordination. That makes the framework
              relevant for role fit, advancement, succession, and long-range
              institutional strength.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Capability-Building and Development */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1350">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1351">
          <div className="flex items-center gap-4 mb-6" data-id="element-1352">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1353">
              <TrendingUp size={24} data-id="element-1354" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1355">
              Capability-Building and Development
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1356">
            A major value of NCJI at the organizational level is its ability to
            connect insight to practical development. It helps institutions
            identify the capabilities that need strengthening and connect them
            to more intentional pathways for coaching, support, training,
            mentorship, and structured responsibility. This makes the framework
            useful not only for assessment, but for building stronger people
            within stronger institutions.
          </p>
        </motion.div>
      </section>

      {/* Leadership Pipelines and Responsibility */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-1357">
        <div className="max-w-4xl mx-auto" data-id="element-1358">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1359">
            <div className="flex items-center gap-4 mb-6" data-id="element-1360">
              <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1361">
                <Users size={24} data-id="element-1362" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1363">
                Leadership Pipelines and Responsibility
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1364">
              As people move into roles with greater consequence, organizations
              need more than confidence or visibility to judge readiness. NCJI
              supports a more rigorous understanding of leadership development
              by focusing on reasoning quality, consequence awareness, emotional
              regulation, communication, systems thinking, and responsibility at
              scale. This can help make succession and leadership cultivation
              more structured and more reliable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Institutional Resilience */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1365">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1366">
          <div className="flex items-center gap-4 mb-6" data-id="element-1367">
            <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1368">
              <Shield size={24} data-id="element-1369" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1370">
              Institutional Resilience
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1371">
            Stronger institutions are built on a stronger human layer. NCJI
            helps organizations identify where capability gaps, weak support
            systems, or shallow leadership depth may be undermining stability
            and performance. Over time, this supports better continuity,
            stronger teams, more resilient leadership structures, and healthier
            institutional development.
          </p>
        </motion.div>
      </section>
    </div>;
}